import React from "react";
import introimg from "../../Assets/home-maincopy.svg";

/* ─── Data ─────────────────────────────────────────────────── */
const techStack = [
  { icon: "🐍", name: "Python" },
  { icon: "🧠", name: "AI/ML" },
  { icon: "🔗", name: "LLM & RAG" },
  { icon: "⚡", name: "FastAPI" },
  { icon: "🎯", name: "Django/DRF" },
  { icon: "🐘", name: "PostgreSQL" },
  { icon: "☁️", name: "GCP/AWS" },
];

const floatingStats = [
  { value: "60+", label: "APIs Delivered", color: "var(--accent)" },
  { value: "3+", label: "Years Experience", color: "var(--accent2)" },
  { value: "70+", label: "AI Solutions", color: "var(--gold)" },
];

/* ─── Component ─────────────────────────────────────────────── */
export default function Home2() {
  return (
    <section className="about-section" id="about">
      {/* CSS-only background elements — NO canvas, NO click-blocking */}
      <div className="about-bg" aria-hidden="true">
        <div className="about-orb about-orb-a" />
        <div className="about-orb about-orb-b" />
      </div>

      <div className="about-inner">
        {/* ── Left column — description ── */}
        <div className="about-left">
          <span className="section-tag">› about me</span>
          <h2 className="section-heading">
            Building AI Systems<br />
            <em>That Scale</em>
          </h2>
          <p className="section-sub">
            I specialize in production-ready AI systems —
            from LLM agents and RAG pipelines to scalable APIs
            that handle real traffic.
          </p>

          {/* Tech stack */}
          <div className="tech-section">
            <p className="tech-label">Tech Stack</p>
            <div className="tech-grid">
              {techStack.map((tech) => (
                <div key={tech.name} className="tech-badge">
                  <span className="tech-icon">{tech.icon}</span>
                  <span className="tech-name">{tech.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA box */}
          <div className="about-cta">
            <p className="about-cta-title">
              <span className="cta-icon">🚀</span> Automate Your Systems — Free Trial Available!
            </p>
            <p className="about-cta-sub">
              Have an old system that needs modernization?
              <span className="highlight-green"> Get a free trial</span> or{" "}
              <span className="highlight-accent">free consultation</span> to see how AI can streamline your workflow.
            </p>
          </div>

          {/* Hire Me button */}
          <a
            href="#contacts"
            className="cta-primary"
            onClick={(e) => { e.preventDefault(); document.getElementById("contacts")?.scrollIntoView({ behavior: "smooth" }); }}
          >
            Hire Me
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        {/* ── Right column — avatar & stats ── */}
        <div className="about-right">
          <div className="avatar-wrapper">
            <div className="avatar-frame">
              <img src={introimg} alt="AI Engineer Avatar" className="about-img" />
            </div>
            <div className="avatar-glow" aria-hidden="true" />
          </div>

          {/* Floating stats */}
          <div className="floating-stats">
            {floatingStats.map((stat) => (
              <div key={stat.label} className="stat-card">
                <span className="stat-value" style={{ color: stat.color }}>{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════
          STYLES
      ════════════════════════════════════════════════════════ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Outfit:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

        /* ── Section ──────────────────────────────────────────────── */
        .about-section {
          position: relative;
          min-height: 100vh;
          display: flex;
          align-items: center;
          padding: 100px 6vw;
          background: var(--bg);
          overflow: hidden;
        }

        .about-bg {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 0;
        }
        .about-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
        }
        .about-orb-a {
          width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(139,114,255,0.12), transparent 70%);
          top: 15%; left: 5%;
          animation: orbFloat 16s ease-in-out infinite reverse;
        }
        .about-orb-b {
          width: 300px; height: 300px;
          background: radial-gradient(circle, rgba(45,220,180,0.1), transparent 70%);
          bottom: 10%; right: 8%;
          animation: orbFloat 20s ease-in-out infinite;
        }

        /* ── Inner Layout ───────────────────────────────────── */
        .about-inner {
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 1280px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 420px;
          gap: 60px;
          align-items: center;
        }

        /* ── Left Column ────────────────────────────────────── */
        .about-left {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .tech-section {
          margin-top: 12px;
        }
        .tech-label {
          font-family: var(--ff-mono);
          font-size: 0.75rem;
          color: var(--muted);
          letter-spacing: 0.1em;
          text-transform: uppercase;
          margin: 0 0 14px;
        }
        .tech-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .tech-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          background: rgba(255,255,255,0.03);
          border: 1px solid var(--border);
          border-radius: 8px;
          font-family: var(--ff-body);
          font-size: 0.85rem;
          font-weight: 500;
          color: var(--muted);
          transition: border-color 0.25s, background 0.25s;
          cursor: default;
        }
        .tech-badge:hover {
          border-color: var(--accent);
          background: var(--accent-dim);
          color: var(--text);
        }
        .tech-icon {
          font-size: 1rem;
        }
        .tech-name {
          white-space: nowrap;
        }

        /* CTA Box */
        .about-cta {
          padding: 24px;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 14px;
          margin-top: 8px;
        }
        .about-cta-title {
          font-family: var(--ff-body);
          font-size: 1.05rem;
          font-weight: 600;
          color: var(--text);
          margin: 0 0 10px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .cta-icon {
          font-size: 1.2rem;
        }
        .about-cta-sub {
          font-size: 0.92rem;
          color: var(--muted);
          line-height: 1.7;
          margin: 0;
        }
        .highlight-green {
          color: var(--accent2);
          font-weight: 600;
        }
        .highlight-accent {
          color: var(--accent);
          font-weight: 600;
        }

        /* ── Right Column ───────────────────────────────────── */
        .about-right {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 32px;
        }

        .avatar-wrapper {
          position: relative;
          width: 320px;
        }

        .avatar-frame {
          position: relative;
          z-index: 2;
          border-radius: 24px;
          overflow: hidden;
          border: 1px solid var(--border-hi);
          background: var(--surface);
          padding: 20px;
          animation: avatarFloat 8s ease-in-out infinite;
        }
        @keyframes avatarFloat {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-10px); }
        }

        .about-img {
          width: 100%;
          height: auto;
          display: block;
          border-radius: 16px;
        }

        .avatar-glow {
          position: absolute;
          bottom: -30px;
          left: 50%;
          transform: translateX(-50%);
          width: 50%;
          height: 60px;
          background: radial-gradient(ellipse, rgba(139,114,255,0.4), transparent 70%);
          filter: blur(20px);
          pointer-events: none;
          z-index: 1;
        }

        /* Floating Stats */
        .floating-stats {
          display: flex;
          gap: 14px;
          width: 100%;
          justify-content: center;
        }

        .stat-card {
          flex: 1;
          max-width: 120px;
          padding: 16px 12px;
          background: rgba(255,255,255,0.02);
          border: 1px solid var(--border);
          border-radius: 12px;
          text-align: center;
          transition: transform 0.25s, border-color 0.25s;
        }
        .stat-card:hover {
          transform: translateY(-4px);
          border-color: var(--accent);
        }

        .stat-value {
          display: block;
          font-family: var(--ff-display);
          font-size: 1.8rem;
          font-weight: 600;
          line-height: 1.2;
        }

        .stat-label {
          display: block;
          font-family: var(--ff-mono);
          font-size: 0.65rem;
          color: var(--muted);
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-top: 6px;
        }

        /* ══ Responsive ══════════════════════════════════════════ */
        @media (max-width: 1024px) {
          .about-inner {
            grid-template-columns: 1fr;
            gap: 50px;
          }
          .about-right { order: -1; }
          .avatar-wrapper { width: 280px; }
          .floating-stats { max-width: 400px; }
        }

        @media (max-width: 680px) {
          .about-section { padding: 80px 5vw; }
          .tech-grid { gap: 8px; }
          .tech-badge { padding: 6px 10px; font-size: 0.8rem; }
          .about-cta { padding: 20px; }
          .avatar-wrapper { width: 240px; }
          .floating-stats { flex-wrap: wrap; gap: 10px; }
          .stat-card { max-width: 100px; padding: 12px 10px; }
          .stat-value { font-size: 1.5rem; }
        }
      `}</style>
    </section>
  );
}